/**
 * The finance engine: one list of dated flows, folded into weeks, months and
 * lines. Commitments (payroll, contracts, grant claims) and levers (deferrals,
 * savings, new spends) are both entered as "when" and "how much"; nothing here
 * knows about any particular supplier, amount or date.
 *
 * Money is whole pence throughout. Pure functions, no I/O: the server and the
 * screens run the same code.
 */
import { addDays, addMonths, dayOfMonth, endOfMonth, maxDate, monthKey, monthsBetween, mondayOf, type ISODate } from "./dates";

export type Repeat = "week" | "month" | "quarter" | "year";
/** Contracted money is owed today and drives the gate; expected money (a run-rate) is shown beside it, never in it. */
export type Basis = "contracted" | "expected";
export type Direction = "in" | "out";
export type GateState = "green" | "amber" | "red" | "stop";

/** When and how much: one date, or a series from that date. */
export interface Schedule {
  date: ISODate;
  repeat: Repeat | null;
  /** The last date the series may fall on. Null runs on to the end of the forecast. */
  until: ISODate | null;
  /** Pence. */
  amount: number;
}

export interface CommitmentInput {
  id: string;
  direction: Direction;
  basis: Basis;
  /** Amounts are positive; the direction says which way the money goes. */
  schedule: Schedule[];
  /** Only dates after this count (Xero's bills cover the line until then). */
  after?: ISODate | null;
  /** Where these amounts came from, when not entered here. */
  origin?: "xero" | "hub" | null;
}

export interface LeverEntryInput extends Schedule {
  /** Pence, signed: positive adds to cash (money in, or a payment held back), negative takes from it. */
  amount: number;
  /** The commitment this entry adjusts, if any: it then counts as part of that line. */
  commitmentId: string | null;
  /** For new money in that isn't against a line. */
  basis: Basis;
}

export interface LeverInput {
  id: string;
  entries: LeverEntryInput[];
}

export interface Thresholds {
  /** Pence. Green at or above `green`, amber at or above `amber`, red at or above `red`, otherwise stop. */
  green: number;
  amber: number;
  red: number;
}

export interface EngineSettings {
  /** The financial year end the monthly view runs to. */
  yearEnd: ISODate;
  /** How many weeks the gate reads (13). */
  gateWeeks: number;
  /** How many weeks the weekly view and the run-out look ahead (38). */
  runOutWeeks: number;
  thresholds: Thresholds;
}

export interface ForecastInput {
  /** The date of the opening balance. Flows count from the day after. */
  asOf: ISODate;
  /** Pence. */
  opening: number;
  commitments: CommitmentInput[];
  levers: LeverInput[];
  settings: EngineSettings;
}

export interface Flow {
  date: ISODate;
  /** Pence, signed: + into the bank, − out of it. */
  amount: number;
  /** Which side of the books it sits on: a saving against a cost line reduces money out rather than adding money in. */
  side: Direction;
  basis: Basis;
  /** The commitment it belongs to (directly, or a lever entry against it). */
  commitmentId: string | null;
  leverId: string | null;
  origin?: "xero" | "hub" | null;
}

export interface WeekRow {
  /** Monday. */
  start: ISODate;
  open: number;
  in: number;
  out: number;
  close: number;
}

export interface MonthRow {
  /** "2026-10" */
  month: string;
  /** The first day counted (the day after the opening balance, in the first month). */
  from: ISODate;
  to: ISODate;
  in: number;
  out: number;
  close: number;
}

export interface Low {
  start: ISODate;
  close: number;
}

export interface Forecast {
  asOf: ISODate;
  opening: number;
  yearEnd: ISODate;
  /** The last day flows are counted. */
  horizonEnd: ISODate;
  /** Contracted income only: what the gate reads. */
  weeks: WeekRow[];
  /** With expected income too. */
  weeksExpected: WeekRow[];
  months: MonthRow[];
  monthsExpected: MonthRow[];
  low: Low;
  lowExpected: Low;
  gate: GateState;
  /** How far the low sits above the next state down (null at stop). */
  headroom: { next: GateState; amount: number } | null;
  firstOverdrawn: ISODate | null;
  firstOverdrawnExpected: ISODate | null;
  yearEndCash: number;
  yearEndCashExpected: number;
  /** Money out from the day after the opening balance to the end of that month. */
  committedThisMonth: number;
  /** Money out from the day after the opening balance to the year end. */
  costsToYearEnd: number;
}

const MAX_OCCURRENCES = 5000;

/** Every date a schedule falls on after `after` and on or before `to`. */
export function occurrences(s: Schedule, after: ISODate, to: ISODate): ISODate[] {
  const out: ISODate[] = [];
  const last = s.until && s.until < to ? s.until : to;
  if (!s.repeat) {
    if (s.date > after && s.date <= last) out.push(s.date);
    return out;
  }
  const day = dayOfMonth(s.date);
  for (let k = 0; k < MAX_OCCURRENCES; k++) {
    const d =
      s.repeat === "week" ? addDays(s.date, 7 * k) : s.repeat === "month" ? addMonths(s.date, k, day) : s.repeat === "quarter" ? addMonths(s.date, 3 * k, day) : addMonths(s.date, 12 * k, day);
    if (d > last) break;
    if (d > after) out.push(d);
  }
  return out;
}

/** The last day the forecast counts: the year end, or the end of the run-out window if that's later. */
export function horizonEndOf(asOf: ISODate, settings: Pick<EngineSettings, "yearEnd" | "runOutWeeks">): ISODate {
  return maxDate(settings.yearEnd, addDays(mondayOf(addDays(asOf, 1)), settings.runOutWeeks * 7 - 1));
}

/** Commitments and levers as one dated list, sorted by date. */
export function buildFlows(input: Pick<ForecastInput, "asOf" | "commitments" | "levers" | "settings">): Flow[] {
  const end = horizonEndOf(input.asOf, input.settings);
  const sides = new Map(input.commitments.map((c) => [c.id, c]));
  const flows: Flow[] = [];
  for (const c of input.commitments) {
    const sign = c.direction === "in" ? 1 : -1;
    const basis = c.direction === "out" ? "contracted" : c.basis;
    const after = c.after && c.after > input.asOf ? c.after : input.asOf;
    for (const s of c.schedule)
      for (const date of occurrences(s, after, end)) flows.push({ date, amount: sign * s.amount, side: c.direction, basis, commitmentId: c.id, leverId: null, origin: c.origin ?? null });
  }
  for (const l of input.levers)
    for (const e of l.entries) {
      const line = e.commitmentId ? sides.get(e.commitmentId) : undefined;
      const side: Direction = line ? line.direction : e.amount < 0 ? "out" : "in";
      const basis: Basis = line ? (line.direction === "out" ? "contracted" : line.basis) : side === "out" ? "contracted" : e.basis;
      for (const date of occurrences(e, input.asOf, end)) flows.push({ date, amount: e.amount, side, basis, commitmentId: line ? line.id : null, leverId: l.id });
    }
  return flows.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Money in and money out of a set of flows. A saving against a cost line reduces "out" rather than adding to "in". */
function inOut(flows: Flow[]): { in: number; out: number } {
  let i = 0;
  let o = 0;
  for (const f of flows) {
    if (f.side === "in") i += f.amount;
    else o -= f.amount;
  }
  return { in: i, out: o };
}

const counted = (f: Flow, expected: boolean) => expected || f.basis === "contracted";

/** Monday-start weeks, from the week containing the day after the opening balance. */
export function weeklyRows(flows: Flow[], asOf: ISODate, opening: number, n: number, expected: boolean): WeekRow[] {
  const first = mondayOf(addDays(asOf, 1));
  const rows: WeekRow[] = [];
  let bal = opening;
  let k = 0;
  for (let w = 0; w < n; w++) {
    const start = addDays(first, 7 * w);
    const end = addDays(start, 6);
    const inWeek: Flow[] = [];
    while (k < flows.length && flows[k]!.date <= end) {
      if (counted(flows[k]!, expected)) inWeek.push(flows[k]!);
      k++;
    }
    const { in: i, out: o } = inOut(inWeek);
    rows.push({ start, open: bal, in: i, out: o, close: bal + i - o });
    bal += i - o;
  }
  return rows;
}

/** Calendar months from the opening balance's month to `to`; the first month counts from the day after the balance. */
export function monthlyRows(flows: Flow[], asOf: ISODate, opening: number, to: ISODate, expected: boolean): MonthRow[] {
  let bal = opening;
  return monthsBetween(addDays(asOf, 1), to).map((month) => {
    const { in: i, out: o } = inOut(flows.filter((f) => monthKey(f.date) === month && counted(f, expected)));
    bal += i - o;
    const first = `${month}-01`;
    return { month, from: first > asOf ? first : addDays(asOf, 1), to: endOfMonth(first), in: i, out: o, close: bal };
  });
}

export function gateState(v: number, t: Thresholds): GateState {
  return v >= t.green ? "green" : v >= t.amber ? "amber" : v >= t.red ? "red" : "stop";
}

/** The next state down from where `v` sits, and how far above its threshold `v` is. */
export function headroomOf(v: number, t: Thresholds): Forecast["headroom"] {
  const s = gateState(v, t);
  if (s === "green") return { next: "amber", amount: v - t.green };
  if (s === "amber") return { next: "red", amount: v - t.amber };
  if (s === "red") return { next: "stop", amount: v - t.red };
  return null;
}

/** The lowest weekly close (the first, where two weeks tie). */
export function lowOf(rows: WeekRow[]): Low {
  let m = rows[0]!;
  for (const r of rows) if (r.close < m.close) m = r;
  return { start: m.start, close: m.close };
}

export function forecast(input: ForecastInput): Forecast {
  const { asOf, opening, settings } = input;
  const flows = buildFlows(input);
  const weeks = weeklyRows(flows, asOf, opening, settings.runOutWeeks, false);
  const weeksExpected = weeklyRows(flows, asOf, opening, settings.runOutWeeks, true);
  const months = monthlyRows(flows, asOf, opening, settings.yearEnd, false);
  const monthsExpected = monthlyRows(flows, asOf, opening, settings.yearEnd, true);
  const gateWindow = weeks.slice(0, Math.max(1, settings.gateWeeks));
  const low = lowOf(gateWindow);
  const lowExpected = lowOf(weeksExpected.slice(0, Math.max(1, settings.gateWeeks)));
  const thisMonthEnd = endOfMonth(asOf);
  const out = (until: ISODate) => inOut(flows.filter((f) => f.date <= until && f.side === "out")).out;
  return {
    asOf,
    opening,
    yearEnd: settings.yearEnd,
    horizonEnd: horizonEndOf(asOf, settings),
    weeks,
    weeksExpected,
    months,
    monthsExpected,
    low,
    lowExpected,
    gate: gateState(low.close, settings.thresholds),
    headroom: headroomOf(low.close, settings.thresholds),
    firstOverdrawn: weeks.find((w) => w.close < 0)?.start ?? null,
    firstOverdrawnExpected: weeksExpected.find((w) => w.close < 0)?.start ?? null,
    yearEndCash: months.length ? months[months.length - 1]!.close : opening,
    yearEndCashExpected: monthsExpected.length ? monthsExpected[monthsExpected.length - 1]!.close : opening,
    committedThisMonth: out(thisMonthEnd),
    costsToYearEnd: out(settings.yearEnd),
  };
}

/**
 * By line and month, for the cost planner: key → month key → pence, read the
 * way the line reads (money out positive on a cost line). Lever entries against
 * a line count in it; the rest are keyed `lever:<id>:<in|out>`.
 */
export function byLine(flows: Flow[], months: string[]): Map<string, Map<string, number>> {
  const rows = new Map<string, Map<string, number>>();
  const keep = new Set(months);
  for (const f of flows) {
    const key = f.commitmentId ?? `lever:${f.leverId}:${f.side}`;
    const m = monthKey(f.date);
    if (!keep.has(m)) continue;
    const row = rows.get(key) ?? new Map<string, number>();
    // stored the way the line reads: money out as a positive figure for a cost line
    row.set(m, (row.get(m) ?? 0) + (f.side === "out" ? -f.amount : f.amount));
    rows.set(key, row);
  }
  return rows;
}

/** The net cash a lever moves inside the forecast window: zero for a pure change of timing. */
export function leverNet(lever: LeverInput, asOf: ISODate, settings: Pick<EngineSettings, "yearEnd" | "runOutWeeks">, until?: ISODate): number {
  const end = until ?? horizonEndOf(asOf, settings);
  let n = 0;
  for (const e of lever.entries) n += e.amount * occurrences(e, asOf, end).length;
  return n;
}

export interface LeverEffect {
  leverId: string;
  /** Net cash moved to the year end (pence); zero when it only changes timing. */
  net: number;
  /** The forecast without this lever and with it, whichever is the current state. */
  without: Pick<Forecast, "low" | "gate" | "firstOverdrawn" | "yearEndCash">;
  with: Pick<Forecast, "low" | "gate" | "firstOverdrawn" | "yearEndCash">;
}

const brief = (f: Forecast) => ({ low: f.low, gate: f.gate, firstOverdrawn: f.firstOverdrawn, yearEndCash: f.yearEndCash });

/**
 * What a lever does, measured against everything else in the forecast:
 * the base plus the other levers in `others`.
 */
export function leverEffect(input: Omit<ForecastInput, "levers">, lever: LeverInput, others: LeverInput[]): LeverEffect {
  const rest = others.filter((l) => l.id !== lever.id);
  const without = forecast({ ...input, levers: rest });
  const withIt = forecast({ ...input, levers: [...rest, lever] });
  return { leverId: lever.id, net: leverNet(lever, input.asOf, input.settings, input.settings.yearEnd), without: brief(without), with: brief(withIt) };
}

export interface SalesNeeded {
  /** Month keys, from the opening balance's month to the year end. */
  months: string[];
  /** New money needed in each month (pence). */
  needed: number[];
  /** Running total needed by the start of each month. */
  byMonth: number[];
  total: number;
  /** With a missed month: how far below the floor cash falls that month (pence, positive), or null. */
  dip: number | null;
}

/**
 * New money that has to land by the start of each month to keep every week's
 * contracted close at or above a floor (Maggie's sales_need). `floorFor` gives
 * the floor for a week (by its Monday); `miss` is the index of a month with no
 * sales, whose amount rolls into the next.
 */
export function salesNeeded(f: Forecast, floorFor: (weekStart: ISODate) => number, miss: number | null = null): SalesNeeded {
  const months = f.months.map((m) => m.month);
  const R = months.map(() => 0);
  for (const w of f.weeks) {
    if (w.start > f.yearEnd) continue;
    const k = months.indexOf(monthKey(w.start));
    if (k < 0) continue;
    // the week the year ends in counts to the year end, not past it
    const close = addDays(w.start, 6) > f.yearEnd ? f.yearEndCash : w.close;
    const short = floorFor(w.start) - close;
    if (short > R[k]!) R[k] = short;
  }
  for (let k = 1; k < R.length; k++) R[k] = Math.max(R[k]!, R[k - 1]!);
  for (let k = 0; k < R.length; k++) R[k] = Math.max(0, R[k]!);
  const N = R.map((v, k) => v - (k ? R[k - 1]! : 0));
  let dip: number | null = null;
  if (miss !== null && miss >= 0 && miss < N.length - 1) {
    dip = N[miss]!;
    N[miss + 1]! += N[miss]!;
    N[miss] = 0;
  }
  return { months, needed: N, byMonth: R, total: R.length ? R[R.length - 1]! : 0, dip };
}
